import { useEffect, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import "./Authentication.css";
import AuthRegisterFlow from "../../components/Authentication/AuthRegisterFlow";
import AuthenticationService from "../../services/AuthenticationService";
import { parseRegisterError, validateRegisterInput } from "../../utils/registerErrorHandler";
import { parseVerificationCodeError, validateVerificationCodeInput } from "../../utils/verificationCodeErrorHandler";
import AuthLoginForm from "../../components/Authentication/AuthLoginForm";
import { parseLoginError, validateLoginInput } from "../../utils/loginErrorHandler";
import { AuthContext } from "../../context/AuthContext";
import userIcon from "../../assets/auth/user.png";
import envelopeIcon from "../../assets/auth/envelope.png";
import lockIcon from "../../assets/auth/lock.png";
import hashtag from "../../assets/auth/lock-hashtag.png";
import tickIcon from "../../assets/icons/status/Tick.png";
import crossIcon from "../../assets/icons/status/Cross.png";
import eye from "../../assets/auth/eye.png";
import eyeOff from "../../assets/auth/eye-crossed.png";
import { useIsMobile } from "../../hooks/useIsMobile";
import SecondButton from "../../components/Elements/SecondButton/SecondButton";
import AffiliationService from "../../services/AffiliationService";
import { logEvent } from "../../analytics/analytics";

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile(770);
  const { setToken, setFirebaseToken } = useContext(AuthContext);
  const [rememberMe, setRememberMe] = useState(false);
  const [verificationStage, setVerificationStage] = useState("form");
  const [isMounted, setIsMounted] = useState(true);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [termsError, setTermsError] = useState(null);
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  const [error, setError] = useState(null);
  const [registerStatus, setRegisterStatus] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [affiliateCode, setAffiliateCode] = useState(null);

  useEffect(() => {
    if (!isMounted) return;

    const loadAffiliate = async () => {
      const searchParams = new URLSearchParams(location.search);
      const code = searchParams.get("a");
      if (code) {
        setAffiliateCode(code);
        try {
          await AffiliationService.markAffiliateLinkEntry(code);
        } catch (error) {}
      }
    };

    loadAffiliate();
  }, [isMounted, location.search]);

  useEffect(() => {
    setVerificationStage("form");
    setRegisterStatus(null);
    setError(null);
    setTermsError(null);
    setUsername("");
    setEmail("");
    setPassword("");
    setVerificationCode("");
    setNewsletterOptIn(false);
  }, []);

  useEffect(() => () => setIsMounted(false), []);

  const handleSendVerificationEmail = async () => {
    setTermsError(null);
    setError("");
    const validationError = validateRegisterInput(username, email, password);
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      await AuthenticationService.sendVerificationCode(email);
      setVerificationStage("code");
    } catch (err) {
      const message = parseRegisterError(err);
      setError(message);
    }
  };

  const handleVerifyRegistrationCode = async () => {
    setError("");
    const validationError = validateVerificationCodeInput(verificationCode);
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      await AuthenticationService.verifyEmailCode(email, verificationCode);
      await AuthenticationService.registerUser(email, password, username, affiliateCode, newsletterOptIn);

      setVerificationStage("form");
      setEmail(email);
      setRegisterStatus("success");
      logEvent("auth", "register", `success`);
    } catch (err) {
      const message = parseVerificationCodeError(err);
      setRegisterStatus("error");
    }
  };

  const handleLogin = async () => {
    setError("");
    const validationError = validateLoginInput(email, password);
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      const response = await AuthenticationService.loginUser(email, password);
      setToken(response.access_token);
      setFirebaseToken(response.firebase_token);

      localStorage.setItem("token", response.access_token);
      localStorage.setItem("firebaseToken", response.firebase_token);

      if (rememberMe) {
        localStorage.setItem("Email", email);
        localStorage.setItem("Password", password);
      } else {
        localStorage.removeItem("Email");
        localStorage.removeItem("Password");
      }

      navigate("/shop");
    } catch (err) {
      const message = parseLoginError(err.response);
      setError(message);
    }
  };
  const onPasswordChangeWrapper = (inputValue) => {
    const value = inputValue?.target?.value || inputValue || "";
    setPassword(value);
  };

  return (
    <>
      <Helmet>
        <title>Telviora - Sign Up</title>
      </Helmet>
      <div className="auth-container">
        <div className="welcome-section">
          {registerStatus === "success" ? (
            <>
              <div className="status-header">
                <img src={tickIcon} alt="Success" className="welcome-status-icon" />
                <h1>SUCCESS!</h1>
              </div>
              <p className="description">
                Your email has been successfully verified and account registered. <br />
                You can now log in with your credentials.
              </p>
            </>
          ) : registerStatus === "error" ? (
            <>
              <div className="status-header">
                <img src={crossIcon} alt="Error" className="welcome-status-icon" />
                <h1>OOOPS!</h1>
              </div>
              <p className="description">Something went wrong. Please check your verification code and try again.</p>
            </>
          ) : (
            <>
              <h1>Join Telviora!</h1>
              <p className="description">Create your account to access exclusive deals and seamless travel services.</p>
              <SecondButton className="toggle-register-desktop" onClick={() => navigate("/sign-in")}>
                Have Account? Sign In
              </SecondButton>
            </>
          )}
        </div>

        <div className="sign-in-section">
          {registerStatus === "success" ? (
            <AuthLoginForm
              envelopeIcon={envelopeIcon}
              lockIcon={lockIcon}
              eye={eye}
              eyeOff={eyeOff}
              email={email}
              password={password}
              showPassword={showPassword}
              error={error}
              isMobile={isMobile}
              onEmailChange={setEmail}
              onPasswordChange={onPasswordChangeWrapper}
              onTogglePassword={() => setShowPassword(!showPassword)}
              onLogin={handleLogin}
              rememberMe={rememberMe}
              onRememberChange={setRememberMe}
              onSwitchToRegister={() => {}}
              successMessage={successMessage}
              setSuccessMessage={setSuccessMessage}
              onCloseError={() => setError(null)}
              onForgotPassword={() => navigate("/forgot-password")}
            />
          ) : (
            <AuthRegisterFlow
              affiliateCode={affiliateCode}
              verificationStage={verificationStage}
              userIcon={userIcon}
              envelopeIcon={envelopeIcon}
              lockIcon={lockIcon}
              hashtag={hashtag}
              eye={eye}
              eyeOff={eyeOff}
              username={username}
              email={email}
              password={password}
              setError={setError}
              termsError={termsError}
              onCloseTermsError={() => setTermsError(null)}
              newsletterOptIn={newsletterOptIn}
              onNewsletterOptInChange={setNewsletterOptIn}
              verificationCode={verificationCode}
              showPassword={showPassword}
              error={error}
              isMobile={isMobile}
              onUsernameChange={setUsername}
              onEmailChange={setEmail}
              onPasswordChange={setPassword}
              onVerificationCodeChange={setVerificationCode}
              onTogglePassword={() => setShowPassword(!showPassword)}
              onSendVerificationEmail={handleSendVerificationEmail}
              onVerifyRegistrationCode={handleVerifyRegistrationCode}
              onSwitchToLogin={() => navigate("/sign-in")}
              onBackToForm={() => {
                setVerificationStage("form");
                setError(null);
              }}
              onCloseError={() => setError(null)}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default Register;
